import React, { createContext, useContext, useState, ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import styles from './Dialog.module.scss';
import {
  ContactDetailsUpdateWarning,
  DetailsUpdatedSuccessDialog,
  ErrorDialog,
  NewApplicationSuccessDialog,
  SignupSuccessDialog,
} from './Dialogs';

type DialogType =
  | 'success'
  | 'error'
  | 'detailsUpdated'
  | 'contactDetailsUpdateWarning'
  | null;

interface DialogContextProps<T = any> {
  showDialog: (
    type: DialogType,
    dialogBody:
      | 'SignupSuccessDialog'
      | 'NewApplicationSuccessDialog'
      | 'ErrorDialog'
      | 'DetailsUpdatedDialog'
      | 'ContactDetailsUpdateWarningDialog',
    message?: string
  ) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [dialogBody, setDialogBody] = useState<
    | 'SignupSuccessDialog'
    | 'NewApplicationSuccessDialog'
    | 'ErrorDialog'
    | 'DetailsUpdatedDialog'
    | 'ContactDetailsUpdateWarningDialog'
    | null
  >(null);
  const [dialogMessage, setDialogMessage] = useState<string>('');

  const showDialog = (
    type: DialogType,
    dialogBody:
      | 'SignupSuccessDialog'
      | 'NewApplicationSuccessDialog'
      | 'ErrorDialog'
      | 'DetailsUpdatedDialog'
      | 'ContactDetailsUpdateWarningDialog',
    message?: string
  ) => {
    setDialogType(type);
    setDialogBody(dialogBody);
    setDialogMessage(message || '');
  };

  const closeDialog = (callback?: () => void) => {
    setDialogType(null);

    // Clear other state after a short delay to let the dialog close animation finish
    setTimeout(() => {
      setDialogBody(null);
      setDialogMessage('');
    }, 200);

    if (callback) {
      callback();
    }
  };

  const renderDialogContent = () => {
    switch (dialogBody) {
      case 'SignupSuccessDialog':
        return <SignupSuccessDialog onClose={closeDialog} />;
      case 'NewApplicationSuccessDialog':
        return <NewApplicationSuccessDialog onClose={closeDialog} />;
      case 'ErrorDialog':
        return <ErrorDialog message={dialogMessage} onClose={closeDialog} />;
      case 'DetailsUpdatedDialog':
        return <DetailsUpdatedSuccessDialog onClose={closeDialog} />;
      case 'ContactDetailsUpdateWarningDialog':
        return <ContactDetailsUpdateWarning onClose={closeDialog} />;
      default:
        return null;
    }
  };

  return (
    <DialogContext.Provider value={{ showDialog, closeDialog }}>
      {children}
      <Dialog
        open={dialogType !== null}
        onClose={() => {
          closeDialog();
        }}
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => {
              closeDialog();
            }}
            className={styles.closeButton}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {renderDialogContent()}
      </Dialog>
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextProps => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};
